import { z } from "zod";
import {
  HerSkinRunIdSchema,
  HerSkinSessionIdSchema,
  IsoDateTimeSchema,
  StableIdSchema,
} from "./common.js";
import { RunSummarySchema } from "./run.js";

export const RunActivityKindSchema = z.enum(["reasoning", "tool", "status", "risk"]);
export const RunActivityStatusSchema = z.enum(["running", "completed", "failed", "notice"]);

export const RunActivitySchema = z
  .object({
    id: StableIdSchema,
    runId: HerSkinRunIdSchema,
    kind: RunActivityKindSchema,
    status: RunActivityStatusSchema,
    title: z.string().min(1).max(160),
    /** 只保存经过 HerSkin 脱敏与限长后的展示摘要，不保存原始工具参数/结果。 */
    detail: z.string().max(32_000).nullable(),
    toolName: z.string().min(1).max(120).nullable(),
    durationMs: z.number().int().min(0).max(86_400_000).nullable(),
    startedAt: IsoDateTimeSchema,
    completedAt: IsoDateTimeSchema.nullable(),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const RunActionStatusSchema = z.enum([
  "pending",
  "submitting",
  "resolved",
  "expired",
  "cancelled",
  "unknown",
]);

export const ApprovalChoiceSchema = z.enum(["once", "session", "always", "deny"]);

const RunActionBaseSchema = z.object({
  id: StableIdSchema,
  runId: HerSkinRunIdSchema,
  status: RunActionStatusSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});

export const ApprovalRunActionSchema = RunActionBaseSchema.extend({
  type: z.literal("approval"),
  description: z.string().min(1).max(2_000),
  command: z.string().max(8_000).nullable(),
  choices: z.array(ApprovalChoiceSchema).min(1).max(4),
  selectedChoice: ApprovalChoiceSchema.nullable(),
}).strict();

export const ClarificationRunActionSchema = RunActionBaseSchema.extend({
  type: z.literal("clarification"),
  question: z.string().min(1).max(4_000),
  choices: z.array(z.string().min(1).max(500)).max(20),
  multiSelect: z.boolean(),
  answer: z.string().max(4_000).nullable(),
}).strict();

export const RunActionSchema = z.discriminatedUnion("type", [
  ApprovalRunActionSchema,
  ClarificationRunActionSchema,
]);

export const RunControlStateSchema = z
  .object({
    sessionId: HerSkinSessionIdSchema,
    run: RunSummarySchema.nullable(),
    activities: z.array(RunActivitySchema).max(100),
    actions: z.array(RunActionSchema).max(50),
    canStop: z.boolean(),
    observedAt: IsoDateTimeSchema,
  })
  .strict();

export const RespondApprovalRequestSchema = z
  .object({
    choice: ApprovalChoiceSchema,
    confirm: z.enum(["respond_approval", "persist_approval"]),
  })
  .strict()
  .superRefine((value, context) => {
    // `always` 会写入 Hermes 的持久允许规则，必须使用更强的显式确认字面量，
    // 避免普通“允许一次”请求被篡改 choice 后升级成永久授权。
    if (value.choice === "always" && value.confirm !== "persist_approval") {
      context.addIssue({
        code: "custom",
        path: ["confirm"],
        message: "始终允许需要单独确认",
      });
    }
    if (value.choice !== "always" && value.confirm !== "respond_approval") {
      context.addIssue({
        code: "custom",
        path: ["confirm"],
        message: "当前批准方式与确认类型不匹配",
      });
    }
  });

export const RespondClarificationRequestSchema = z
  .object({
    answer: z.string().trim().min(1).max(4_000),
    confirm: z.literal("respond_clarification"),
  })
  .strict();

export const StopRunRequestSchema = z
  .object({
    confirm: z.literal("stop_run"),
  })
  .strict();

export type RunActivity = z.infer<typeof RunActivitySchema>;
export type RunAction = z.infer<typeof RunActionSchema>;
export type ApprovalChoice = z.infer<typeof ApprovalChoiceSchema>;
export type RunControlState = z.infer<typeof RunControlStateSchema>;
export type RespondApprovalRequest = z.infer<typeof RespondApprovalRequestSchema>;
export type RespondClarificationRequest = z.infer<typeof RespondClarificationRequestSchema>;
export type StopRunRequest = z.infer<typeof StopRunRequestSchema>;
